import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  List,
  ListItem,
  Stack,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { usePermissions } from '@/auth/usePermissions';
import type { CorrectionRecord, CorrectionDialogResult } from '@/types/monitoring';
import {
  useValidateCorrectFailedOperationMutation,
  useRejectOperationCorrectionMutation,
} from './api';
import { getProviderIconSrc } from './providerIcon';

interface Props {
  correction: CorrectionRecord;
  onClose: (result: CorrectionDialogResult | null) => void;
}

export function CorrectionDetailsModal({ correction, onClose }: Props) {
  const { t } = useTranslation();
  const { hasAny } = usePermissions();

  const validateMutation = useValidateCorrectFailedOperationMutation();
  const rejectMutation = useRejectOperationCorrectionMutation();

  const iconSrc = getProviderIconSrc(correction.providerIdentifier, correction.transferType);

  const handleValidate = async () => {
    const res = await validateMutation.mutateAsync(correction.id);
    onClose({ code: res.code, message: res.message, type: 'validate' });
  };

  const handleReject = async () => {
    const res = await rejectMutation.mutateAsync(correction.id);
    onClose({ code: res.code, message: res.message, type: 'reject' });
  };

  const DetailRow = ({ label, value }: { label: string; value: string | null | undefined }) => (
    <ListItem disableGutters>
      <Stack direction="row" justifyContent="space-between" width="100%">
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 160 }}>
          {label}
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {value ?? 'None.'}
        </Typography>
      </Stack>
    </ListItem>
  );

  const isPending = correction.status.toLowerCase() === 'pending';

  return (
    <Dialog open onClose={() => onClose(null)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ textAlign: 'center' }}>
        {iconSrc && (
          <Box
            component="img"
            src={iconSrc}
            alt="provider"
            sx={{ width: 32, height: 32, mr: 1, verticalAlign: 'middle' }}
          />
        )}
        {moment(correction.correctionDate).format('DD/MM/YYYY H:mm')}
      </DialogTitle>
      <DialogContent>
        <List dense>
          <DetailRow
            label={t('pages.monitoring.corrections.table.ticket_id', { defaultValue: 'Ticket ID' })}
            value={correction.ticketId ?? undefined}
          />
          <DetailRow
            label={t('pages.monitoring.corrections.table.correction_type', {
              defaultValue: 'Correction Type',
            })}
            value={t(
              `pages.monitoring.corrections.correction_types.${correction.correctionType.toLowerCase()}`,
              { defaultValue: correction.correctionType },
            )}
          />
          <DetailRow
            label={t('pages.monitoring.corrections.table.phone_number', {
              defaultValue: 'Phone Number',
            })}
            value={correction.providerAccountId}
          />
          <DetailRow
            label={t('pages.monitoring.corrections.table.amount', { defaultValue: 'Amount' })}
            value={`${correction.amount} XAF`}
          />
          <DetailRow
            label={t('pages.monitoring.corrections.table.transaction_type', {
              defaultValue: 'Transaction Type',
            })}
            value={t(
              `pages.monitoring.corrections.transaction_types.${correction.transactionType.toLowerCase()}`,
              { defaultValue: correction.transactionType },
            )}
          />
          <DetailRow
            label={t('pages.monitoring.corrections.table.transfer_type', {
              defaultValue: 'Transfer Type',
            })}
            value={
              correction.transactionType === 'TRANSFER'
                ? t(`pages.monitoring.transfers.${correction.transferType.toLowerCase()}`, {
                    defaultValue: correction.transferType,
                  })
                : t('pages.monitoring.payments.payment', { defaultValue: 'Payment' })
            }
          />
          <DetailRow
            label={t('pages.monitoring.corrections.table.user', { defaultValue: 'User' })}
            value={correction.username}
          />
        </List>

        {isPending && hasAny(['edit:rejectValidateCorrection']) && (
          <>
            <Divider sx={{ my: 1 }} />
            <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 1 }}>
              <Button
                variant="contained"
                color="error"
                onClick={() => void handleReject()}
                disabled={rejectMutation.isPending || validateMutation.isPending}
              >
                {t('pages.monitoring.corrections.reject_correction', {
                  defaultValue: 'Reject',
                })}
              </Button>
              <Button
                variant="contained"
                color="success"
                onClick={() => void handleValidate()}
                disabled={validateMutation.isPending || rejectMutation.isPending}
              >
                {t('pages.monitoring.corrections.validate_correction', {
                  defaultValue: 'Validate',
                })}
              </Button>
            </Stack>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
